import {
  CARRIAGE_RETURN,
  FULL_CUT,
  LINE_FEED,
  PRINTER_INIT,
  RASTER_BIT_IMAGE,
  RASTER_BIT_IMAGE_MODE_NORMAL,
  BEEP_COMMAND_SIMPLE,
  BEEP_COMMAND_ADVANCED
} from './escpos-codes';

interface BeepConfig {
  beepEnabled?: boolean;
  numberOfBeeps?: number;
  lineFeeds?: number;
}

/**
 * Convert HTMLCanvasElement to ESC/POS buffer
 * Adapted from silent-printer
 */
const canvasToEscpos = (canvas: HTMLCanvasElement, config?: BeepConfig): Uint8Array => {
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Failed to get canvas context');
  }

  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);

  const width = canvas.width;
  const height = canvas.height;

  const bytes = new Uint8Array((width * height) >> 3);
  const readPixel = (x: number, y: number): number => {
    return data[(width * y + x) * 4] < 200 ? 1 : 0;
  };

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x += 8) {
      for (let b = 0; b < 8; b++) {
        bytes[y * (width >> 3) + (x >> 3)] |= readPixel(x + b, y) << (7 - b);
      }
    }
  }

  const header = [
    ...PRINTER_INIT,
    ...new Array(2).fill(LINE_FEED - 4),
    CARRIAGE_RETURN,
    ...RASTER_BIT_IMAGE,
    RASTER_BIT_IMAGE_MODE_NORMAL,
    (width >> 3) & 0xff,
    ((width >> 3) >> 8) & 0xff,
    height & 0xff,
    (height >> 8) & 0xff
  ];

  // Create beep commands if enabled in config
  let beepCommands: number[] = [];
  if (config?.beepEnabled) {
    const numberOfBeeps = config.numberOfBeeps || 2;
    const beepTime = 1; // 1 * 100ms = 100ms per beep
    
    // Use both beep command formats for better printer compatibility
    // First format: ESC B n t (older printers)
    const simpleBeepCommand = [...BEEP_COMMAND_SIMPLE, numberOfBeeps, beepTime];
    
    // Second format: ESC ( A pL pH fn a n m (newer printers)
    const advancedBeepCommand = [...BEEP_COMMAND_ADVANCED, numberOfBeeps, beepTime];
    
    // Combine both commands so at least one should work depending on printer model
    beepCommands = [...simpleBeepCommand, ...advancedBeepCommand];
    console.log(`[CanvasToEscpos] Adding ${numberOfBeeps} beeps to print job with both command formats for compatibility`);
  }
  
  const lineFeeds = config?.lineFeeds == null ? 3 : config.lineFeeds;
  const footer = [...new Array(Math.max(0, lineFeeds)).fill(LINE_FEED), ...beepCommands, ...FULL_CUT];
  const buffer = new Uint8Array(header.length + bytes.length + footer.length);
  buffer.set(header, 0);
  buffer.set(bytes, header.length);
  buffer.set(footer, buffer.length - footer.length);
  
  return buffer;
};

export default canvasToEscpos;

